import { createApi } from "@reduxjs/toolkit/query/react";
import { authBaseQuery } from "./auth/authBaseQuery";
import {
	ICallList,
	IResponseCallingList,
	ITimeInterval,
	IResponseConfigsList,
	IResponseCallingListsState,
	ILogsResponse,
	IListStatus,
	IInstances,
	IJobsResponse,
} from "@/types/clsTypes";
import { IResponse } from "@/types/statusTypes";
import { ENDPOINTS_CLS, ENDPOINTS_SES, URL_CLS, URL_SES } from "@/constants/api_endpoints";
import { IDialogDataResponse } from "@/types/ses/sesArchiveTypes";

type UpdateCallingListPayload = {
	original: ICallList;
	changes: Partial<ICallList>;
};

export const clsApi = createApi({
	reducerPath: "clsApi",
	baseQuery: authBaseQuery,
	tagTypes: ["CallingLists", "TimeIntervals", "Configs", "CallingProcess", "Jobs"],
	endpoints: (builder) => ({
		getCallingLists: builder.query<IResponseCallingList, Record<string, string> | undefined>({
			query: (params) => {
				const filteredParams = Object.fromEntries(
					Object.entries(params || {})
						.filter(([_, value]) => value !== undefined && value !== null && value !== "")
						.map(([key, val]) => [key, val === "-" ? "" : val])
				);

				return {
					url: `${URL_CLS}/${ENDPOINTS_CLS.CALLING_LINE_LISTS}`,
					params: filteredParams,
				};
			},
			providesTags: ["CallingLists"],
		}),
		getCallingList: builder.query<{ error: 0 | 1; list: ICallList }, { id: string }>({
			query: ({ id }) => {
				return {
					url: `${URL_CLS}/${ENDPOINTS_CLS.CALLING_LINE_LIST}/${id}`,
				};
			},
			providesTags: ["CallingLists"],
		}),
		getTimeintervals: builder.query<{ error: number; timeintervals: ITimeInterval[] }, void>({
			query: () => {
				return {
					url: `${URL_CLS}/${ENDPOINTS_CLS.CALLING_LINE_TIMEINTERVALS_LIST}`,
				};
			},
			providesTags: () => [{ type: "TimeIntervals", id: "LIST" }],
		}),
		getCallingListsConfigs: builder.query<IResponseConfigsList, void>({
			query: () => {
				return {
					url: `${URL_CLS}/${ENDPOINTS_CLS.CALLING_LINE_CONFIG_LIST}`,
				};
			},
			providesTags: () => [{ type: "Configs", id: "LIST" }],
		}),
		getCallingListsStates: builder.query<IResponseCallingListsState, void>({
			query: () => {
				return {
					url: `${URL_CLS}/${ENDPOINTS_CLS.CALLING_LINE_INSTANCES_LIST}`,
				};
			},
			providesTags: () => [{ type: "CallingProcess", id: "LIST" }],
		}),
		getCallingListLogs: builder.query<ILogsResponse, { id: string; date: string }>({
			query: ({ id, date }) => {
				return {
					url: `${URL_CLS}/${ENDPOINTS_CLS.CALLING_LINE_LOGS}`,
					params: { id, date },
				};
			},
			providesTags: (_, __, arg) => [{ type: "CallingProcess", id: arg.id }],
		}),
		getListStatus: builder.query<IListStatus, { id: string }>({
			query: ({ id }) => {
				return {
					url: `${URL_CLS}/${ENDPOINTS_CLS.CALLING_LINE_LIST_STATUS}/${id}`,
				};
			},
			providesTags: [{ type: "CallingProcess", id: "LIST" }],
		}),
		getListInstance: builder.query<IInstances, { id: string }>({
			query: ({ id }) => {
				return {
					url: `${URL_CLS}/${ENDPOINTS_CLS.CALLING_LINE_LIST_INSTANCE}/${id}`,
				};
			},
			providesTags: (_, __, arg) => [
				{ type: "CallingProcess", id: arg.id },
				{ type: "CallingProcess", id: "LIST" },
			],
		}),
		getListJobs: builder.query<IJobsResponse, { id: string }>({
			query: ({ id }) => {
				return {
					url: `${URL_CLS}/${ENDPOINTS_CLS.CALLING_LINE_JOBS_LIST}/${id}`,
				};
			},
			providesTags: () => [{ type: "Jobs", id: "LIST" }],
		}),

		getDialogInfo: builder.query<IDialogDataResponse, { id: string }>({
			query: ({ id }) => {
				return {
					url: `${URL_SES}/${ENDPOINTS_SES.ARCHIVE_DIALOG}/${id}`,
				};
			},
			providesTags: () => [],
		}),

		getDialogAudioInfo: builder.query<IDialogDataResponse, { id: string }>({
			query: ({ id }) => {
				return {
					url: `${URL_SES}/${ENDPOINTS_SES.ARCHIVE_AUDIO}/${id}`,
				};
			},
			providesTags: () => [],
		}),

		resumeJobs: builder.mutation<{ jobs_count: number } & IResponse, { id: string }>({
			query: ({ id }) => {
				return {
					url: `${URL_CLS}/${ENDPOINTS_CLS.CALLING_LINE_JOBS_RESUME}/${id}`,
					method: "POST",
				};
			},
			invalidatesTags: () => [
				{ type: "Jobs", id: "LIST" },
				{ type: "CallingProcess", id: "LIST" },
			],
		}),

		pauseJobs: builder.mutation<{ jobs_count: number } & IResponse, { id: string }>({
			query: ({ id }) => {
				return {
					url: `${URL_CLS}/${ENDPOINTS_CLS.CALLING_LINE_JOBS_PAUSE}/${id}`,
					method: "POST",
				};
			},
			invalidatesTags: () => [
				{ type: "Jobs", id: "LIST" },
				{ type: "CallingProcess", id: "LIST" },
			],
		}),

		runScriptForList: builder.mutation<IResponse, { id: string }>({
			query: ({ id }) => {
				return {
					url: `${URL_CLS}/${ENDPOINTS_CLS.CALLING_LINE_RUN_SCRIPT_FOR_LIST}/${id}`,
					method: "POST",
				};
			},
			invalidatesTags: () => ["CallingLists"],
		}),

		exportPhonesList: builder.mutation<Blob, { listId: string }>({
			query: ({ listId }) => {
				return {
					url: `${URL_CLS}/${ENDPOINTS_CLS.CALLING_LINE_EXPORT_CALLING_LIST}/${listId}`,
					method: "GET",
					responseHandler: (response) => response.blob(),
				};
			},
		}),
		importPhonesList: builder.mutation<{ error: 1 | 0; message: string }, { listId: string; removeDuplicates: boolean; formData: FormData }>({
			query: ({ listId, removeDuplicates, formData }) => ({
				url: `${URL_CLS}/${ENDPOINTS_CLS.CALLING_LINE_IMPORT_CALLING_LIST}/${listId}?remove_duplicates=${removeDuplicates}`,
				method: "POST",
				body: formData,
			}),
			invalidatesTags: ["CallingLists"],
		}),

		addCallingList: builder.mutation<IResponse & { id: string }, { newListData: Omit<ICallList, "id"> }>({
			query: ({ newListData }) => {
				return {
					url: `${URL_CLS}/${ENDPOINTS_CLS.CALLING_LINE_ADD_CALLING_LIST}`,
					method: "POST",
					body: newListData,
				};
			},
			invalidatesTags: ["CallingLists"],
		}),
		singleCall: builder.mutation<IResponse, { callParams: Pick<ICallList, "source" | "limits"> | { destination: string; metadata: object } }>({
			query: ({ callParams }) => {
				return {
					url: `${URL_CLS}/${ENDPOINTS_CLS.CALLING_LINE_SINGLE_CALL}`,
					method: "POST",
					body: callParams,
				};
			},
			invalidatesTags: ["CallingLists"],
		}),

		updateCallingListsConfigs: builder.mutation<IResponse, { configId: string; newConfigs: IResponseConfigsList["data"]["1"] | IResponseConfigsList["data"]["1"] }>({
			query: ({ configId, newConfigs }) => {
				return {
					url: `${URL_CLS}/${ENDPOINTS_CLS.CALLING_LINE_MODIFY_CONFIG_LIST}/${configId}`,
					method: "PUT",
					body: newConfigs,
				};
			},
			invalidatesTags: () => [{ type: "Configs", id: "LIST" }],
		}),

		updateCallingList: builder.mutation<IResponse, UpdateCallingListPayload>({
			query: ({ original, changes }) => {
				const updated: ICallList = {
					...original,
					...changes,
					limits: changes.limits
						? {
								...original.limits,
								...(changes.limits ?? {}),
						  }
						: original.limits,
					channel_parameters: changes.channel_parameters
						? {
								...original.channel_parameters,
								...changes.channel_parameters,
						  }
						: original.channel_parameters,
					scheduler: changes.scheduler ?? original.scheduler,
					timeintervals: changes.timeintervals ?? original.timeintervals,
				};

				return {
					url: `${URL_CLS}/${ENDPOINTS_CLS.CALLING_LINE_MODIFY_CALLING_LIST}/${original.id}`,
					method: "PUT",
					body: updated,
				};
			},
			invalidatesTags: ["CallingLists", "Jobs", "CallingProcess"],
		}),
		updateCallingListTimeintervals: builder.mutation<ITimeInterval, { intervalId: string; newTimeintervals: ITimeInterval }>({
			query: ({ intervalId, newTimeintervals }) => {
				return {
					url: `${URL_CLS}/${ENDPOINTS_CLS.CALLING_LINE_MODIFY_TIMEINTERVALS_LIST}/${intervalId}`,
					method: "PUT",
					body: newTimeintervals,
				};
			},
			invalidatesTags: () => [{ type: "TimeIntervals", id: "LIST" }],
		}),
		startCallingList: builder.mutation<IResponse, { listId: string; repeat?: boolean }>({
			query: ({ listId, repeat }) => ({
				url: `${URL_CLS}/${ENDPOINTS_CLS.CALLING_LINE_START_CALLING}`,
				method: "POST",
				params: {
					repeat,
				},
				body: { id: listId },
			}),
			invalidatesTags: (_, __, arg) => [{ type: "CallingProcess", id: arg.listId }, "CallingProcess", "CallingLists"],
		}),

		stopCallingList: builder.mutation<IResponse, { listId: string }>({
			query: ({ listId }) => ({
				url: `${URL_CLS}/${ENDPOINTS_CLS.CALLING_LINE_STOP_CALLING}`,
				method: "POST",
				body: { id: listId },
			}),
			invalidatesTags: (_, __, arg) => [{ type: "CallingProcess", id: arg.listId }],
		}),
		scheduleCallingList: builder.mutation<void, { listId: string }>({
			query: ({ listId }) => ({
				url: `${URL_CLS}/${ENDPOINTS_CLS.CALLING_LINE_SCHEDULE_CALLING}/${listId}`,
				method: "POST",
			}),
			invalidatesTags: (_, __, arg) => [{ type: "CallingProcess", id: arg.listId }],
		}),

		deleteCallingList: builder.mutation<IResponse, { listId: string }>({
			query: ({ listId }) => ({
				url: `${URL_CLS}/${ENDPOINTS_CLS.CALLING_LINE_DELETE_CALLING_LIST}/${listId}`,
				method: "DELETE",
			}),
			invalidatesTags: ["CallingLists"],
		}),

		addTimeInterval: builder.mutation<IResponse & { id: string }, { timeintervals: ITimeInterval }>({
			query: ({ timeintervals }) => ({
				url: `${URL_CLS}/${ENDPOINTS_CLS.CALLING_LINE_ADD_TIMEINTERVAL}`,
				method: "POST",
				body: timeintervals,
			}),
			invalidatesTags: () => [{ type: "TimeIntervals", id: "LIST" }],
		}),

		deleteTimeInterval: builder.mutation<IResponse & { id: string }, { timeIntervalId: string }>({
			query: ({ timeIntervalId }) => ({
				url: `${URL_CLS}/${ENDPOINTS_CLS.CALLING_LINE_DELETE_TIMEINTERVAL}/${timeIntervalId}`,
				method: "DELETE",
			}),
			invalidatesTags: () => [{ type: "TimeIntervals", id: "LIST" }],
		}),
		deleteInstance: builder.mutation<IResponse & { id: string }, { instanceId: string }>({
			query: ({ instanceId }) => ({
				url: `${URL_CLS}/${ENDPOINTS_CLS.CALLING_LINE_INSTANCES_DELETE_INSTANCE}/${instanceId}`,
				method: "DELETE",
			}),
			invalidatesTags: () => [{ type: "CallingProcess", id: "LIST" }],
		}),
	}),
});

export const {
	useGetCallingListsQuery,
	useGetCallingListQuery,
	useGetTimeintervalsQuery,
	useGetCallingListsConfigsQuery,
	useGetCallingListsStatesQuery,
	useGetCallingListLogsQuery,
	useGetListStatusQuery,
	useGetListInstanceQuery,
	useGetListJobsQuery,
	useGetDialogInfoQuery,
	useGetDialogAudioInfoQuery,
	useResumeJobsMutation,
	usePauseJobsMutation,
	useRunScriptForListMutation,
	useExportPhonesListMutation,
	useImportPhonesListMutation,
	useAddCallingListMutation,
	useSingleCallMutation,
	useUpdateCallingListsConfigsMutation,
	useUpdateCallingListMutation,
	useUpdateCallingListTimeintervalsMutation,
	useStartCallingListMutation,
	useStopCallingListMutation,
	useScheduleCallingListMutation,
	useDeleteCallingListMutation,
	useAddTimeIntervalMutation,
	useDeleteTimeIntervalMutation,
	useDeleteInstanceMutation,
} = clsApi;
